import React, { useState, useEffect } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Flex } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { PositionStatus } from "../cellStatus/PositionStatus";
import { PositionData } from "../types";
import {
  iconContainer,
  indentedContainer,
  cellWrapper,
  flexContainer,
  regularContentContainer,
} from "./styles_vanilla.css";

// TODO: Add integration tests for this component after API integration
// The component has complex expand/collapse state management and interactions
// that would benefit from integration testing with the actual grid implementation

interface PositionCellRendererProps extends ICellRendererParams {
  data: PositionData;
}

const PositionCellRenderer: React.FC<PositionCellRendererProps> = ({ data, node }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(node?.expanded || false);

  // Update local state when node.expanded changes externally
  useEffect(() => {
    if (node) {
      setIsExpanded(node.expanded || false);
    }
  }, [node, node?.expanded]);

  if (!data) return null;

  const isGroupRow = data.successorCases && data.successorCases.length > 0;
  const isChildRow = Boolean(data.parentId);

  const isNotAttached = isChildRow
    ? false
    : !data.linkedOnboardingCase ||
      ((!data.linkedOnboardingCase.profiles ||
        data.linkedOnboardingCase.profiles.length === 0) &&
        !data.linkedOnboardingCase.investingProfile);

  const handleExpandClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    // For group rows, expand/collapse
    if (node && isGroupRow) {
      const newExpandedState = !node.expanded;
      node.setExpanded(newExpandedState);
      setIsExpanded(newExpandedState);
    }
  };

  const renderGroupRowContent = () => {
    const gpName = data.externalArenaName || "";
    return (
      <Flex className={flexContainer}>
        <Flex className={iconContainer}>
          <Icon
            type={isExpanded ? "chevron-down" : "chevron-right"}
            onClick={handleExpandClick}
            data-testid={isExpanded ? "chevron-down-icon" : "chevron-right-icon"}
          />
        </Flex>
        <PositionStatus
          status="simplified"
          client={gpName}
          entity="payment change review"
          positionId={data.positionId?.toString() || ""}
          gpMainUrl={data.gpMainUrl}
        />
      </Flex>
    );
  };

  const renderRegularRowContent = () => {
    return (
      <Flex className={regularContentContainer}>
        {isChildRow && <div className={indentedContainer} />}
        <PositionStatus
          status={isNotAttached ? "not-attached" : "chart"}
          client={data.complianceCaseName || "--"}
          entity={data.investmentEntity.name}
          positionId={data.positionId?.toString() || ""}
          gpMainUrl={data.gpMainUrl}
        />
      </Flex>
    );
  };

  return (
    <Flex className={cellWrapper}>
      {isGroupRow ? renderGroupRowContent() : renderRegularRowContent()}
    </Flex>
  );
};

export default PositionCellRenderer;
